// src/components/AllClients.jsx
import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import useDebounce from "../hook/useDebounce";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";
import { usePermissions } from "../context/PermissionContext";
import Avatar from "react-avatar";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import { TextWrap } from "./ui/TextWrap";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";

const AllClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "client_id",
    direction: "desc",
  });
  const { hasPermission } = usePermissions();
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const navigate = useNavigate();

  const fetchClients = async (
    page = 1,
    perPage = 10,
    search = "",
    sortBy = "client_id",
    sortOrder = "desc"
  ) => {
    try {
      setLoading(true);
      setErrorMessage("");
      const endpoint = `/get-all-clients?search=${encodeURIComponent(
        search
      )}&page=${page}&per_page=${perPage}&sort_by=${sortBy}&sort_order=${sortOrder}`;
      const response = await apiCall("get", endpoint, null, navigate);

      if (response?.data?.success) {
        const clientList = response.data.data || [];
        const pagination = response.data.pagination || {};
        setClients(clientList);
        setTotalEntries(pagination.total || clientList.length);
        setCurrentPage(parseInt(pagination.current_page || page, 10));
        setTotalPages(Math.ceil(pagination.total / perPage) || 1);
        setPerPage(parseInt(pagination.per_page || perPage, 10));
      } else {
        setErrorMessage("Failed to load clients.");
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
      setErrorMessage(error?.message || "Error fetching clients.");
    } finally {
      setLoading(false);
    }
  };

  const getClientLogo = (url, clientName) => {
    if (url && url.trim() !== "") {
      return `https://pevbackend.codovio.com/public/uploads/client_logos/${url}`;
    }
    return null;
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // fetchClients(page, perPage, searchQuery, sortConfig.key, sortConfig.direction); // YEH LINE DELETE KARO
    }
  };

  const handleEntriesPerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
    // fetchClients(1, value, searchQuery, sortConfig.key, sortConfig.direction); // YEH LINE DELETE KARO
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedClients = useMemo(() => {
    return [...clients].sort((a, b) => {
      let valueA, valueB;

      switch (sortConfig.key) {
        case "client_id":
          valueA = a.client_id || 0;
          valueB = b.client_id || 0;
          break;
        case "client_name":
          valueA = (a.client_name || "").toLowerCase();
          valueB = (b.client_name || "").toLowerCase();
          break;
          case "company_name":
          valueA = (a.company_name || "").toLowerCase();
          valueB = (b.company_name || "").toLowerCase();
          break;
        case "email":
          valueA = (a.email || "").toLowerCase();
          valueB = (b.email || "").toLowerCase();
          break;
        case "phone_no":
          valueA = (a.phone_no || "").toLowerCase();
          valueB = (b.phone_no || "").toLowerCase();
          break;
        case "client_type":
          valueA = (a.client_type || "").toLowerCase();
          valueB = (b.client_type || "").toLowerCase();
          break;
        case "plan_name":
          valueA = (a.plan_name || "").toLowerCase();
          valueB = (b.plan_name || "").toLowerCase();
          break;
        case "plan_start_date":
          valueA = new Date(a.plan_start_date || "1970-01-01");
          valueB = new Date(b.plan_start_date || "1970-01-01");
          break;
        case "plan_end_date":
          valueA = new Date(a.plan_end_date || "1970-01-01");
          valueB = new Date(b.plan_end_date || "1970-01-01");
          break;
        case "created_by":
          valueA = (a.created_by || "").toLowerCase();
          valueB = (b.created_by || "").toLowerCase();
          break;
        case "created_datetime":
          valueA = new Date(a.created_datetime || "1970-01-01");
          valueB = new Date(b.created_datetime || "1970-01-01");
          break;
        case "is_active":
          valueA = a.is_active ? "Active" : "Inactive";
          valueB = b.is_active ? "Active" : "Inactive";
          break;
        default:
          return 0;
      }

      if (valueA < valueB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valueA > valueB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [clients, sortConfig]);

  useEffect(() => {
    fetchClients(
      currentPage,
      perPage,
      debouncedSearchQuery,
      sortConfig.key,
      sortConfig.direction
    );
  }, [
    currentPage,
    perPage,
    debouncedSearchQuery,
    sortConfig.key,
    sortConfig.direction,
  ]);

  const startIndex = totalEntries > 0 ? (currentPage - 1) * perPage + 1 : 0;
  const endIndex = Math.min(currentPage * perPage, totalEntries);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (endPage - startPage < maxPagesToShow - 1) {
      if (startPage === 1)
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      else if (endPage === totalPages)
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  if (errorMessage) {
    return <ErrorAlert />;
  }

  return (
    <div className="p-4 p-md-3 p-sm-2">
      <div className="card shadow-sm">
        <div className="card p-3 p-sm-2 p-md-3">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div
              className="icon-field flex-grow-1"
              style={{ maxWidth: "400px" }}
            >
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search By Client Name, Email, Phone No or Client Type"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="icon">
                <Icon icon="ion:search-outline" />
              </span>
            </div>
            <PerPageDropdown
              perPage={perPage}
              handlePerPageChange={(value) => {
                setPerPage(value);
                setCurrentPage(1);
                // fetchClients(1, value, searchQuery);
              }}
            />
            {hasPermission("insert_client") && (
              <div className="ms-auto">
                <Link
                  to="/add-client"
                  className="btn btn-primary-600 btn-sm d-flex align-items-center"
                  // style={{ backgroundColor: "#475EAB", color: "white" }}
                >
                  <Icon icon="ri:add-line" className="me-2" /> Add Client
                </Link>
              </div>
            )}
          </div>

          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show mb-3"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
              ></button>
            </div>
          )}

          {/* {sortedClients.length === 0 && !loading ? (
            <div className="alert alert-warning" role="alert">
              No clients found.
            </div>
          ) : ( */}
          {sortedClients.length === 0 && !loading ? (
            <TableDataNoAvailable message="No Client found." />
          ) : (
            // Step 1: Replace your table container div
            <div className="position-relative">
              <div
                               className="table-responsive scroll-sm"

                style={{
                  opacity: loading ? 0.4 : 1,
                  pointerEvents: loading ? "none" : "auto",
                  overflowX: "auto",
                 
                }}
              >
                <table
                  className="table bordered-table mb-0 table-hover"
                  style={{
                    minWidth: "1400px", // Proper minimum width
                    whiteSpace: "nowrap", // Prevent text wrapping
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#475EAB", color: "white" }}>
                      <th
                        scope="col"
                        className="p-2"
                        style={{ minWidth: "60px" }}
                      >
                        <span>S.No</span>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("client_name")}
                        className="p-2"
                        style={{ cursor: "pointer", minWidth: "200px" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Company Name</span>
                          <SortIcons
                            field="client_name"
                            sortConfig={{
                              field: sortConfig.key,
                              direction: sortConfig.direction,
                            }}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("company_name")}
                        className="p-2"
                        style={{ cursor: "pointer", minWidth: "200px" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Client Name</span>
                          <SortIcons
                            field="company_name"
                            sortConfig={{
                              field: sortConfig.key,
                              direction: sortConfig.direction,
                            }}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("email")}
                        className="p-2"
                        style={{ cursor: "pointer", minWidth: "200px" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Email</span>
                          <SortIcons
                            field="email"
                            sortConfig={{
                              field: sortConfig.key,
                              direction: sortConfig.direction,
                            }}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("phone_no")}
                        className="p-2"
                        style={{ cursor: "pointer", minWidth: "150px" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Phone</span>
                          <SortIcons
                            field="phone_no"
                            sortConfig={{
                              field: sortConfig.key,
                              direction: sortConfig.direction,
                            }}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("client_type")}
                        className="p-2"
                        style={{ cursor: "pointer", minWidth: "120px" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Client Type</span>
                          <SortIcons
                            field="client_type"
                            sortConfig={{
                              field: sortConfig.key,
                              direction: sortConfig.direction,
                            }}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("plan_name")}
                        className="p-2"
                        style={{ cursor: "pointer", minWidth: "120px" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Plan</span>
                          <SortIcons
                            field="plan_name"
                            sortConfig={{
                              field: sortConfig.key,
                              direction: sortConfig.direction,
                            }}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("plan_start_date")}
                        className="p-2"
                        style={{ cursor: "pointer", minWidth: "140px" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Plan Start Date</span>
                          <SortIcons
                            field="plan_start_date"
                            sortConfig={{
                              field: sortConfig.key,
                              direction: sortConfig.direction,
                            }}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("plan_end_date")}
                        className="p-2"
                        style={{ cursor: "pointer", minWidth: "140px" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Plan End Date</span>
                          <SortIcons
                            field="plan_end_date"
                            sortConfig={{
                              field: sortConfig.key,
                              direction: sortConfig.direction,
                            }}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("is_active")}
                        className="p-2"
                        style={{ cursor: "pointer", minWidth: "100px" }}
                      >
                        <div className="d-flex ">
                          <span>Status</span>
                          <SortIcons
                            field="is_active"
                            sortConfig={{
                              field: sortConfig.key,
                              direction: sortConfig.direction,
                            }}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("created_by")}
                        className="p-2"
                        style={{ cursor: "pointer", minWidth: "120px" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created By</span>
                          <SortIcons
                            field="created_by"
                            sortConfig={{
                              field: sortConfig.key,
                              direction: sortConfig.direction,
                            }}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("created_datetime")}
                        className="p-2"
                        style={{ cursor: "pointer", minWidth: "120px" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created Date</span>
                          <SortIcons
                            field="created_datetime"
                            sortConfig={{
                              field: sortConfig.key,
                              direction: sortConfig.direction,
                            }}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        // style={{ minWidth: "120px" }}
                      >
                        <span>Action</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedClients.map((client, index) => (
                      <tr key={client.client_id}>
                        <td className="p-2 ">{startIndex + index}</td>
                        <td className="p-2 ">
                          <div className="d-flex align-items-center">
                            {client.logo_url &&
                            client.logo_url.trim() !== "" ? (
                              <img
                                loading="lazy"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  objectFit: "cover",
                                }}
                                src={`https://pevbackend.codovio.com/public/uploads/client_logos/${client.logo_url}`}
                                alt={client.company_name || "Client"}
                                className="flex-shrink-0 me-2 radius-8"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextSibling.style.display = "block";
                                }}
                              />
                            ) : null}
                            <Avatar
                              name={client.company_name || "-"}
                              size="40"
                              round={true}
                              className="flex-shrink-0 me-2 "
                              style={{
                                display:
                                  client.logo_url &&
                                  client.logo_url.trim() !== ""
                                    ? "none"
                                    : "block",
                              }}
                            />
                            <Link
                              to={`/view-client/${client.client_id}`}
                              className="text-truncate"
                              style={{ maxWidth: "150px" }}
                            >
                              {/* {client.client_name || "N/A"} */}
                              <TextWrap
                                location={client.company_name || "-"}
                              />
                            </Link>
                          </div>
                        </td>
                        <td
                          className="p-2 text-truncate"
                          style={{ maxWidth: "200px" }}
                        >
                          {client.client_name || "-"}
                        </td>
                        <td
                          className="p-2 text-truncate"
                          style={{ maxWidth: "200px" }}
                        >
                          {client.email || "-"}
                        </td>
                        <td
                          className="p-2 text-truncate"
                          style={{ maxWidth: "150px" }}
                        >
                          {client.phone_no || "-"}
                        </td>
                        <td className="p-2">{client.client_type || "-"}</td>
                        <td className="p-2  text-primary-600">
                          <Link to="/all-plans">
                            {client.plan_name || "-"}
                          </Link>
                        </td>
                        <td className="p-2">
                          {client.plan_start_date
                            ? new Date(
                                client.plan_start_date
                              ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })
                            : "-"}
                        </td>
                        <td className="p-2 ">
                          {client.plan_end_date
                            ? new Date(client.plan_end_date).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                }
                              )
                            : "-"}
                        </td>
                        <td className="p-2 ">
                          <span
                            className={`px-3 py-1 radius-4 fw-medium text-sm ${
                              client.is_active
                                ? "bg-success-focus text-success-600 border border-success-main"
                                : "bg-danger-focus text-danger-600 border border-danger-main"
                            }`}
                          >
                            {client.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="p-2">{client.created_by || "-"}</td>
                        <td className="p-2">
                          {client.created_datetime
                            ? new Date(
                                client.created_datetime
                              ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })
                            : "-"}
                        </td>
                        <td className="p-2">
                          <Link to={`/view-client/${client.client_id}`}>
                            <button
                              className="w-32-px h-32-px me-2 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
                              style={{
                                position: "relative",
                                transition: "all 0.3s ease",
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.1)";

                                const tooltip = document.createElement("div");
                                tooltip.textContent = "View Detail";
                                tooltip.style.cssText = `
                      position: absolute;
                      top: -35px;
                      left: 50%;
                      transform: translateX(-50%);
                      background-color: #333;
                      color: white;
                      padding: 5px 8px;
                      border-radius: 4px;
                      font-size: 12px;
                      white-space: nowrap;
                      z-index: 1000;
                      pointer-events: none;
                    `;
                                tooltip.id = "view-tooltip";
                                e.currentTarget.appendChild(tooltip);
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                const tooltip =
                                  document.getElementById("view-tooltip");
                                if (tooltip) tooltip.remove();
                              }}
                            >
                              <Icon icon="iconamoon:eye-light" />
                            </button>
                          </Link>
                          {hasPermission("update_client") && (
                            <Link to={`/update-client/${client.client_id}`}>
                              <button
                                className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                style={{
                                  position: "relative",
                                  transition: "all 0.3s ease",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "scale(1.1)";

                                  const tooltip = document.createElement("div");
                                  tooltip.textContent = "Edit";
                                  tooltip.style.cssText = `
                        position: absolute;
                        top: -35px;
                        left: 50%;
                        transform: translateX(-50%);
                        background-color: #333;
                        color: white;
                        padding: 5px 8px;
                        border-radius: 4px;
                        font-size: 12px;
                        white-space: nowrap;
                        z-index: 1000;
                        pointer-events: none;
                      `;
                                  tooltip.id = "edit-tooltip";
                                  e.currentTarget.appendChild(tooltip);
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "scale(1)";
                                  const tooltip =
                                    document.getElementById("edit-tooltip");
                                  if (tooltip) tooltip.remove();
                                }}
                              >
                                <Icon icon="lucide:edit" />
                              </button>
                            </Link>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {loading && (
                <div
                  className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                  style={{
                    zIndex: 10,
                    borderRadius: "10px",
                    padding: "20px",
                  }}
                >
                  <LoadingComponent />
                </div>
              )}

              <PaginationControls
                pagination={{
                  currentPage,
                  perPage,
                  total: totalEntries,
                }}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllClients;
